import { useEffect, useId, useRef, useState } from 'react';
import { ChevronRight, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CitationChip } from '@/components/ui';
import { Confidence } from './Confidence';
import type { Citation } from '@/lib/api/types';
import './AnswerView.css';

/** Below this score the answer is flagged with the low-confidence warning band. */
const LOW_CONFIDENCE_THRESHOLD = 0.6;

export interface AnswerViewProps {
  answer: string;
  sources: Citation[];
  /** Null for an answer read back from the history, which keeps no score: the
   *  band is left out rather than shown as a made-up zero. */
  confidence: number | null;
  /** Optional handler when an inline citation marker is activated. */
  onCitation?: (source: Citation) => void;
}

/**
 * Several chunks of one document merged into a single cited source — the RAG
 * market standard: one row per source DOCUMENT, never one per chunk.
 */
interface SourceGroup {
  /** Grouping key (document id; the uri for web results — see groupKey). */
  key: string;
  /** First citation of the document; carries the title and feeds onCitation. */
  representative: Citation;
  /** Chunk ordinals behind the answer, sorted and de-duplicated. */
  ordinals: number[];
  /** First valid http(s) uri of a WEB result in the group — the only case that earns a link. */
  webLink: string | null;
}

function isWebUri(uri: string | null | undefined): uri is string {
  return Boolean(uri && /^https?:\/\//.test(uri));
}

/**
 * Only a web result is a link. A catalog document's uri is a storage key (or, for
 * older rows, whatever the source was registered with — a Drive URL included),
 * and a citation must never become a way to fetch a file: downloads live in
 * Documentos, behind the ownership check.
 */
function linkOf(source: Citation): string | null {
  return source.documentId === 'web' && isWebUri(source.uri) ? source.uri : null;
}

function groupKey(source: Citation): string {
  // Web citations all share the sentinel documentId "web" (one per result URL),
  // so they group by uri — merging them would collapse distinct sources.
  if (source.documentId === 'web') return source.uri ?? source.documentTitle;
  return source.documentId || source.documentTitle;
}

function groupByDocument(sources: Citation[]): SourceGroup[] {
  const groups = new Map<string, SourceGroup>();
  for (const source of sources) {
    const key = groupKey(source);
    let group = groups.get(key);
    if (!group) {
      group = { key, representative: source, ordinals: [], webLink: null };
      groups.set(key, group);
    }
    if (typeof source.ordinal === 'number' && !group.ordinals.includes(source.ordinal)) {
      group.ordinals.push(source.ordinal);
    }
    group.webLink ??= linkOf(source);
  }
  for (const group of groups.values()) {
    group.ordinals.sort((a, b) => a - b);
  }
  return [...groups.values()];
}

/** "185, 186, 187, 234, 235" → "185–187, 234, 235" (runs of 3+ collapse). */
function formatOrdinals(ordinals: number[]): string {
  const parts: string[] = [];
  let start = 0;
  for (let i = 1; i <= ordinals.length; i += 1) {
    if (i < ordinals.length && ordinals[i] === ordinals[i - 1]! + 1) continue;
    const from = ordinals[start]!;
    const to = ordinals[i - 1]!;
    parts.push(to === from ? `${from}` : to === from + 1 ? `${from}, ${to}` : `${from}–${to}`);
    start = i;
  }
  return parts.join(', ');
}

/**
 * Renders a generated answer with its provenance: the prose, an inline citation
 * marker per source document, the confidence band, and the source list grouped
 * by document. Trust is the product — an answer never appears here without a
 * way to reach its sources, and a low-confidence answer is visibly flagged for
 * human verification. A source renders as an external link ONLY when it is a web
 * result carrying a valid http(s) uri; a catalog document renders as its title —
 * never a dead anchor, and never a download. The list is a disclosure, collapsed
 * in every new answer; an inline marker opens it and brings its row into view.
 */
export function AnswerView({ answer, sources, confidence, onCitation }: AnswerViewProps): JSX.Element {
  const lowConfidence = confidence !== null && confidence < LOW_CONFIDENCE_THRESHOLD;
  const groups = groupByDocument(sources);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  // The source list is secondary to the prose: collapsed until asked for.
  const [sourcesOpen, setSourcesOpen] = useState(false);
  // A fresh object per marker click, so clicking the same [n] twice re-reveals it.
  const [revealed, setRevealed] = useState<{ index: number } | null>(null);

  useEffect(() => {
    if (!revealed) return;
    const row = listRef.current?.children[revealed.index];
    if (!(row instanceof HTMLElement)) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    row.focus({ preventScroll: true });
    row.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
  }, [revealed]);

  function revealSource(index: number, source: Citation): void {
    setSourcesOpen(true);
    setRevealed({ index });
    onCitation?.(source);
  }

  return (
    <div className="answer">
      <div className="answer__prose">
        {/* Models answer in markdown (headings, bold, lists). react-markdown
            renders it as elements — raw HTML stays escaped, so model output can
            never inject markup into the page. */}
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{answer}</ReactMarkdown>
        <p className="answer__citations">
          {groups.map((group, i) => (
            <CitationChip
              key={group.key}
              className="answer__inline-cite"
              documentTitle={group.representative.documentTitle}
              ordinal={i + 1}
              snippet={group.representative.snippet}
              inline
              onClick={() => revealSource(i, group.representative)}
            />
          ))}
        </p>
      </div>

      {confidence !== null && (
        <div className="answer__meta">
          <Confidence value={confidence} />
          {lowConfidence && (
            <span className="answer__review" role="note">
              Confiança baixa. Verifique as fontes antes de usar esta resposta.
            </span>
          )}
        </div>
      )}

      {groups.length > 0 ? (
        <div className="answer__sources">
          <button
            type="button"
            className="answer__sources-title"
            aria-expanded={sourcesOpen}
            aria-controls={listId}
            onClick={() => setSourcesOpen((open) => !open)}
          >
            <ChevronRight className="answer__sources-chevron" aria-hidden="true" />
            <span>Fontes citadas</span> <span>({groups.length})</span>
          </button>
          <ul id={listId} ref={listRef} className="answer__sources-list" role="list" hidden={!sourcesOpen}>
            {groups.map((group, i) => (
              <li
                key={group.key}
                className="answer__source"
                tabIndex={-1}
                data-revealed={revealed?.index === i || undefined}
              >
                <span className="answer__source-marker" aria-hidden="true">
                  [{i + 1}]
                </span>
                <FileText className="answer__source-icon" aria-hidden="true" />
                <span className="answer__source-title">{group.representative.documentTitle}</span>
                {/* The person who indexed a document can see it was used. */}
                {group.representative.ownedByMe && (
                  <span className="answer__source-mine">da sua base</span>
                )}
                {group.ordinals.length > 0 && (
                  <span className="answer__source-chunks">
                    trechos {formatOrdinals(group.ordinals)}
                  </span>
                )}
                {group.webLink && (
                  <a
                    className="answer__source-link"
                    href={group.webLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Abrir fonte ↗
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="answer__no-source" role="note">
          Esta resposta não citou fontes da base de conhecimento. Trate-a com cautela e confirme
          antes de repassar.
        </p>
      )}
    </div>
  );
}
